// Where a cleaner was when they started or stopped the timer, compared with the site.
// Sites have only a text address, so the site point is its UK postcode centre
// (postcodes.io, free open data). A postcode covers a small area, so "on site" allows some slack.

const ON_SITE_METERS = 300;
const LOW_ACCURACY_METERS = 300;
const POSTCODE_LOOKUP_URL = 'https://api.postcodes.io/postcodes';
const POSTCODE_BATCH_SIZE = 100;
const POSTCODE_PATTERN = /\b([A-Z]{1,2}\d[A-Z\d]?)\s*(\d[A-Z]{2})\b/;

export const extractUkPostcode = (address) => {
	const match = POSTCODE_PATTERN.exec(String(address || '').toUpperCase());
	return match ? `${match[1]} ${match[2]}` : null;
};

const toRadians = (degrees) => (degrees * Math.PI) / 180;

export const distanceMeters = (from, to) => {
	const earthRadius = 6371000;
	const dLat = toRadians(to.latitude - from.latitude);
	const dLng = toRadians(to.longitude - from.longitude);
	const a =
		Math.sin(dLat / 2) ** 2 +
		Math.cos(toRadians(from.latitude)) * Math.cos(toRadians(to.latitude)) * Math.sin(dLng / 2) ** 2;
	return 2 * earthRadius * Math.asin(Math.sqrt(a));
};

export const formatDistance = (meters) =>
	meters < 1000 ? `${Math.max(10, Math.round(meters / 10) * 10)} m` : `${(meters / 1000).toFixed(1)} km`;

// location: { latitude, longitude, accuracy } or null; site: { latitude, longitude } or null.
export const describeWorkLocation = ({ location, site }) => {
	if (!location) return { status: 'missing', label: 'No location' };
	const accuracyNote =
		Number.isFinite(location.accuracy) && location.accuracy > LOW_ACCURACY_METERS
			? ` (±${formatDistance(location.accuracy)})`
			: '';
	if (!site) return { status: 'unknown', label: `Location saved${accuracyNote}` };
	const meters = distanceMeters(location, site);
	if (meters <= ON_SITE_METERS) return { status: 'on-site', label: `On site${accuracyNote}`, meters };
	return { status: 'away', label: `${formatDistance(meters)} from the site${accuracyNote}`, meters };
};

// Postcode centres already looked up in this session; null means the postcode was not found.
const siteCache = new Map();

export const lookupPostcodes = async (postcodes, fetchImpl = globalThis.fetch) => {
	const wanted = [...new Set((postcodes || []).filter(Boolean))];
	const missing = wanted.filter((postcode) => !siteCache.has(postcode));
	for (let start = 0; start < missing.length; start += POSTCODE_BATCH_SIZE) {
		const batch = missing.slice(start, start + POSTCODE_BATCH_SIZE);
		try {
			const response = await fetchImpl(POSTCODE_LOOKUP_URL, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ postcodes: batch }),
			});
			if (!response?.ok) continue;
			const body = await response.json();
			(Array.isArray(body?.result) ? body.result : []).forEach(({ query, result }) => {
				const key = extractUkPostcode(query);
				if (!key) return;
				siteCache.set(
					key,
					result && Number.isFinite(result.latitude) && Number.isFinite(result.longitude)
						? { latitude: result.latitude, longitude: result.longitude }
						: null,
				);
			});
		} catch {
			// Without the lookup the office still sees the saved location and the map link.
		}
	}
	return Object.fromEntries(wanted.map((postcode) => [postcode, siteCache.get(postcode) ?? null]));
};

export const clearPostcodeCache = () => siteCache.clear();
